import { useState } from 'react'
import classnames from 'classnames'
import styles from './ItsmeButton.module.scss'
import ItsmeLogo from './ItsmeLogo'
import { trackEvent } from 'utils/tracking'
import { EVENT_CATEGORIES } from 'constants/tracking'
import { ItsmeState } from 'lib/itsme/types'
import { itsmeLib } from 'lib/index'
import { useStoreSelector } from 'hooks/store'
import { ItsmeButtonProps } from './types'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { useTranslation } from 'next-i18next/pages'
import { log } from 'utils/logging'
import { Roboto_Slab } from 'next/font/google'

const robotoSlab = Roboto_Slab({ subsets: ['latin'], weight: '700', style: 'normal' })

const ItsmeButton = ({ className, isRetry, isConfirmation, ...props }: ItsmeButtonProps) => {
  // Redux store
  const { inputs, propositions } = useStoreSelector((store) => store.customer)
  const marketingStore = useStoreSelector((store) => store.marketing)
  const { selectedProducer, opportunityId } = useStoreSelector((store) => store.app)
  const { deliveryAddress, simulation, meterDetails, personalData } = inputs

  // i18n
  const { t } = useTranslation()

  // Local state
  const [loading, setLoading] = useState<boolean>(false)

  // Router
  const { locale } = useRouter()

  // Constants
  const language = locale === Language.FRENCH ? Language.FRENCH : Language.DUTCH

  const redirectItsme = async () => {
    trackEvent(
      { category: EVENT_CATEGORIES.SUBSCRIPTION, object: 'itsme-identification', action: 'start' },
      { region: simulation.region, retry: isRetry, emailConfirmation: isConfirmation }
    )
    setLoading(true)

    const { firstName, lastName, mobilePhone, email } = personalData

    const state: ItsmeState =
      isConfirmation && opportunityId
        ? {
            opportunityId,
            language,
            emailConfirmation: true
          }
        : {
            opportunityId,
            simulation,
            deliveryAddress,
            meterDetails: {
              counterType: meterDetails.counterType,
              hasSolarPanels: meterDetails.hasSolarPanels,
              walloniaSolarPanelsSince2024: meterDetails.walloniaSolarPanelsSince2024
            },
            personalData: firstName || lastName || mobilePhone || email ? { firstName, lastName, mobilePhone, email } : undefined,
            language,
            producerSlug: selectedProducer,
            marketing: marketingStore,
            propositions
          }

    try {
      const { data, success } = await itsmeLib.authorize(state, language)

      if (success && data.url) {
        window.document.location = data.url
      } else {
        throw new Error('Something went wrong when fetching the itsme url.')
      }
    } catch (error) {
      trackEvent(
        { category: EVENT_CATEGORIES.SUBSCRIPTION, object: 'itsme-identification', action: 'fail' },
        { region: simulation.region, retry: isRetry, reason: 'Could not fetch itsme url' }
      )

      log({
        error: error?.message || 'Caught error on redirectItsme',
        identifier: `[Itsme:redirect]`,
        metaData: {
          error
        }
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      {...props}
      type="button"
      className={classnames(styles['itsme-button'], robotoSlab.className, className)}
      disabled={loading || props.disabled}
      onClick={redirectItsme}
    >
      <ItsmeLogo />
      <span>{t('itsmeButton')}</span>
    </button>
  )
}

export default ItsmeButton
